import type {
  LessonPlanDocxOptions,
  GradeReportDocxOptions,
  AnalyticsReportDocxOptions,
} from './types';
import { extractSlideContent } from './slide-content-extractor';
import { downloadBlob, formatReportDate, calculateGradeStats, getLetterGrade } from './export-helpers';

// ============================================================
// LESSON PLAN WORD DOCUMENT
// ============================================================

export async function generateLessonPlanDocx(options: LessonPlanDocxOptions): Promise<void> {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, PageBreak } = await import('docx');

  const { lesson, slides, includeAnswers = false, teacherName } = options;
  const sortedSlides = [...slides].sort((a, b) => a.slide_order - b.slide_order);

  // Build TOC entries
  const tocEntries = sortedSlides.map((slide, i) => {
    const extracted = extractSlideContent(slide, i);
    return new Paragraph({
      children: [
        new TextRun({ text: `${extracted.slideNumber}. `, bold: true }),
        new TextRun({ text: `${extracted.title}` }),
        new TextRun({ text: `  [${extracted.typeLabel}]`, italics: true, color: '888888' }),
      ],
      spacing: { after: 100 },
    });
  });

  // Build slide sections
  const slideSections: any[] = [];
  sortedSlides.forEach((slide, i) => {
    const extracted = extractSlideContent(slide, i);

    // Slide heading
    slideSections.push(
      new Paragraph({
        children: [
          new TextRun({ text: `Slide ${extracted.slideNumber}: ${extracted.title}` }),
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 100 },
      })
    );

    // Type badge
    slideSections.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `[${extracted.typeLabel}]`,
            italics: true,
            color: '666666',
            size: 20,
          }),
          ...(extracted.points !== undefined
            ? [new TextRun({ text: `  |  Points: ${extracted.points}`, color: '0066CC', size: 20 })]
            : []),
        ],
        spacing: { after: 200 },
      })
    );

    // Main content
    if (extracted.mainText) {
      slideSections.push(
        new Paragraph({
          children: [new TextRun({ text: extracted.mainText })],
          spacing: { after: 150 },
        })
      );
    }

    // Secondary content (options, instructions, etc.)
    if (extracted.secondaryText) {
      const lines = extracted.secondaryText.split('\n');
      lines.forEach((line) => {
        slideSections.push(
          new Paragraph({
            children: [new TextRun({ text: line, color: '444444' })],
            spacing: { after: 60 },
            indent: { left: 360 },
          })
        );
      });
    }

    // Media reference
    if (extracted.mediaUrl) {
      slideSections.push(
        new Paragraph({
          children: [
            new TextRun({ text: `[Media: ${extracted.mediaUrl}]`, italics: true, color: '999999', size: 18 }),
          ],
          spacing: { after: 100 },
        })
      );
    }

    // Answer (if enabled)
    if (includeAnswers && extracted.answerText) {
      slideSections.push(
        new Paragraph({
          children: [
            new TextRun({ text: 'Correct Answer: ', bold: true, color: '228B22' }),
            new TextRun({ text: extracted.answerText, color: '228B22' }),
          ],
          spacing: { after: 200 },
          shading: { type: 'clear' as any, color: 'auto', fill: 'F0FFF0' },
        })
      );
    }

    // Separator
    slideSections.push(
      new Paragraph({
        children: [],
        border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' } },
        spacing: { after: 200 },
      })
    );
  });

  const doc = new Document({
    creator: teacherName || 'EduLive JHS',
    title: `Lesson Plan - ${lesson.title}`,
    sections: [
      {
        properties: {},
        children: [
          // Title
          new Paragraph({
            children: [new TextRun({ text: lesson.title, bold: true, size: 48 })],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
          }),
          // Description
          ...(lesson.description
            ? [
                new Paragraph({
                  children: [new TextRun({ text: lesson.description, italics: true, color: '666666', size: 24 })],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 200 },
                }),
              ]
            : []),
          // Meta info
          new Paragraph({
            children: [
              new TextRun({ text: `Mode: ${lesson.mode === 'live' ? 'Live' : 'Student-Paced'}`, size: 20 }),
              new TextRun({ text: `  |  Slides: ${slides.length}`, size: 20 }),
              new TextRun({ text: `  |  Generated: ${formatReportDate()}`, size: 20 }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),
          ...(teacherName
            ? [
                new Paragraph({
                  children: [new TextRun({ text: `Teacher: ${teacherName}`, size: 20, color: '666666' })],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 400 },
                }),
              ]
            : []),
          // Horizontal line
          new Paragraph({
            children: [],
            border: { bottom: { style: BorderStyle.SINGLE, size: 3, color: '333333' } },
            spacing: { after: 400 },
          }),
          // Table of Contents heading
          new Paragraph({
            children: [new TextRun({ text: 'Table of Contents', bold: true })],
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 },
          }),
          ...tocEntries,
          // Page break before slides
          new Paragraph({ children: [new PageBreak()] }),
          // All slide content
          ...slideSections,
          // Footer
          new Paragraph({
            children: [
              new TextRun({
                text: 'Generated by EduLive JHS',
                italics: true,
                color: 'AAAAAA',
                size: 16,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 600 },
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `lesson-plan-${lesson.title.replace(/[^a-zA-Z0-9]/g, '-')}.docx`);
}

// ============================================================
// GRADE REPORT WORD DOCUMENT
// ============================================================

export async function generateGradeReportDocx(options: GradeReportDocxOptions): Promise<void> {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType } = await import('docx');

  const { lessonTitle, students, generatedDate, teacherName } = options;
  const stats = calculateGradeStats(students);

  const headerCell = (text: string) =>
    new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ text, bold: true, size: 18, color: 'FFFFFF' })],
          alignment: AlignmentType.CENTER,
        }),
      ],
      shading: { type: 'clear' as any, color: 'auto', fill: '2E4057' },
      width: { size: 14, type: WidthType.PERCENTAGE },
    });

  const dataCell = (text: string, fill?: string) =>
    new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ text, size: 18 })],
          alignment: AlignmentType.CENTER,
        }),
      ],
      ...(fill ? { shading: { type: 'clear' as any, color: 'auto', fill } } : {}),
    });

  // Grade table
  const headerRow = new TableRow({
    children: [
      headerCell('Student'),
      headerCell('Score'),
      headerCell('Max'),
      headerCell('%'),
      headerCell('Grade'),
      headerCell('Completion'),
      headerCell('Participation'),
    ],
  });

  const dataRows = students.map(
    (s, i) =>
      new TableRow({
        children: [
          dataCell(s.student_name, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${s.total_score}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${s.max_possible_score}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(s.percentage)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(getLetterGrade(s.percentage), i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(s.completion_rate)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(s.participation_rate)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
        ],
      })
  );

  // Grade distribution
  const gradeDistribution: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  students.forEach((s) => {
    gradeDistribution[getLetterGrade(s.percentage)]++;
  });

  const distRows = Object.entries(gradeDistribution).map(
    ([grade, count]) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: grade, bold: true, size: 20 })],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: `${count}`, size: 20 })],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: stats.totalStudents > 0 ? `${Math.round((count / stats.totalStudents) * 100)}%` : '0%',
                    size: 20,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        ],
      })
  );

  const doc = new Document({
    creator: teacherName || 'EduLive JHS',
    title: `Grade Report - ${lessonTitle}`,
    sections: [
      {
        properties: {},
        children: [
          // Title
          new Paragraph({
            children: [new TextRun({ text: `Grade Report: ${lessonTitle}`, bold: true, size: 40 })],
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Generated: ${formatReportDate(generatedDate)}`, size: 20, color: '888888' }),
              ...(teacherName ? [new TextRun({ text: `  |  Teacher: ${teacherName}`, size: 20, color: '888888' })] : []),
            ],
            spacing: { after: 400 },
          }),

          // Summary stats
          new Paragraph({
            children: [new TextRun({ text: 'Class Summary', bold: true })],
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Total Students: ${stats.totalStudents}`, size: 22 }),
              new TextRun({ text: `  |  Class Average: ${stats.classAverage}%`, size: 22, bold: true }),
              new TextRun({ text: `  |  Highest: ${stats.highest}%`, size: 22 }),
              new TextRun({ text: `  |  Lowest: ${stats.lowest}%`, size: 22 }),
              new TextRun({ text: `  |  Passing (≥70%): ${stats.passingCount}`, size: 22 }),
            ],
            spacing: { after: 400 },
          }),

          // Grade Table
          new Paragraph({
            children: [new TextRun({ text: 'Student Grades', bold: true })],
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 },
          }),
          new Table({
            rows: [headerRow, ...dataRows],
            width: { size: 100, type: WidthType.PERCENTAGE },
          }),

          // Grade Distribution
          new Paragraph({
            children: [new TextRun({ text: 'Grade Distribution', bold: true })],
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          new Table({
            rows: [
              new TableRow({
                children: [
                  headerCell('Grade'),
                  headerCell('Count'),
                  headerCell('Percentage'),
                ],
              }),
              ...distRows,
            ],
            width: { size: 50, type: WidthType.PERCENTAGE },
          }),

          // Footer
          new Paragraph({
            children: [
              new TextRun({ text: 'Generated by EduLive JHS', italics: true, color: 'AAAAAA', size: 16 }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 600 },
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `grade-report-${lessonTitle.replace(/[^a-zA-Z0-9]/g, '-')}.docx`);
}

// ============================================================
// ANALYTICS REPORT WORD DOCUMENT
// ============================================================

export async function generateAnalyticsReportDocx(options: AnalyticsReportDocxOptions): Promise<void> {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType } = await import('docx');

  const { reportTitle, lessonReports, studentReports, sessionAnalytics, generatedDate, teacherName } = options;

  const headerCell = (text: string) =>
    new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ text, bold: true, size: 18, color: 'FFFFFF' })],
          alignment: AlignmentType.CENTER,
        }),
      ],
      shading: { type: 'clear' as any, color: 'auto', fill: '2E4057' },
    });

  const dataCell = (text: string, fill?: string) =>
    new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ text, size: 18 })],
          alignment: AlignmentType.CENTER,
        }),
      ],
      ...(fill ? { shading: { type: 'clear' as any, color: 'auto', fill } } : {}),
    });

  const children: any[] = [
    // Title
    new Paragraph({
      children: [new TextRun({ text: reportTitle, bold: true, size: 40 })],
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 200 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Generated: ${formatReportDate(generatedDate)}`, size: 20, color: '888888' }),
        ...(teacherName ? [new TextRun({ text: `  |  Teacher: ${teacherName}`, size: 20, color: '888888' })] : []),
      ],
      spacing: { after: 400 },
    }),
  ];

  // Lesson Summary section
  if (lessonReports && lessonReports.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Lesson Summary', bold: true })],
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 200 },
      })
    );

    const lessonRows = lessonReports.map((r, i) =>
      new TableRow({
        children: [
          dataCell(r.lesson_title, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(r.mode === 'live' ? 'Live' : 'Student-Paced', i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.total_sessions}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.total_students}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_score)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_completion_rate)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
        ],
      })
    );

    children.push(
      new Table({
        rows: [
          new TableRow({
            children: [
              headerCell('Lesson'),
              headerCell('Mode'),
              headerCell('Sessions'),
              headerCell('Students'),
              headerCell('Avg Score'),
              headerCell('Completion'),
            ],
          }),
          ...lessonRows,
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
      })
    );
  }

  // Student Performance section
  if (studentReports && studentReports.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Student Performance', bold: true })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
      })
    );

    const studentRows = studentReports.map((r, i) =>
      new TableRow({
        children: [
          dataCell(r.student_name, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.sessions_attended}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_percentage)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_completion_rate)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.passing_sessions}/${r.total_graded_sessions}`, i % 2 === 0 ? 'F8F9FA' : undefined),
        ],
      })
    );

    children.push(
      new Table({
        rows: [
          new TableRow({
            children: [
              headerCell('Student'),
              headerCell('Sessions'),
              headerCell('Avg Score'),
              headerCell('Completion'),
              headerCell('Passing'),
            ],
          }),
          ...studentRows,
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
      })
    );
  }

  // Session Analytics section
  if (sessionAnalytics && sessionAnalytics.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Session Analytics', bold: true })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
      })
    );

    const sessionRows = sessionAnalytics.map((r, i) =>
      new TableRow({
        children: [
          dataCell(r.started_at ? new Date(r.started_at).toLocaleDateString() : 'N/A', i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.total_participants}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_score)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${Math.round(r.avg_completion_rate)}%`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.total_submissions}`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.tab_switch_incidents} tab / ${r.app_switch_incidents} app / ${r.screenshot_incidents} shots`, i % 2 === 0 ? 'F8F9FA' : undefined),
          dataCell(`${r.session_duration_minutes} min`, i % 2 === 0 ? 'F8F9FA' : undefined),
        ],
      })
    );

    children.push(
      new Table({
        rows: [
          new TableRow({
            children: [
              headerCell('Date'),
              headerCell('Participants'),
              headerCell('Avg Score'),
              headerCell('Completion'),
              headerCell('Submissions'),
              headerCell('Security'),
              headerCell('Duration'),
            ],
          }),
          ...sessionRows,
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
      })
    );
  }

  // Footer
  children.push(
    new Paragraph({
      children: [
        new TextRun({ text: 'Generated by EduLive JHS', italics: true, color: 'AAAAAA', size: 16 }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 600 },
    })
  );

  const doc = new Document({
    creator: teacherName || 'EduLive JHS',
    title: reportTitle,
    sections: [{ properties: {}, children }],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${reportTitle.replace(/[^a-zA-Z0-9]/g, '-')}.docx`);
}
